import { useState, SyntheticEvent, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CoreBox, CoreTypography } from '@youscience/core';
import { Tab } from '@mui/material';

import { Container } from '@components/Layout/components/Container';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { ExploreModeSwitcher } from '@components/ExploreModeSwitcher/ExploreModeSwitcher';

import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import { PAGE_TABS_DATA, FROM_HASH_TO_MODE, FROM_MODE_TO_HASH } from '@constants/scholarships';

import { getBreadcrumbs } from '@utils/breadcrumbs';

import { CollegeLocation } from '@interfaces/location';

import { sxStyles, StyledNavBar, StyledNavBarTitle, StyledTabs, StyledContentSection } from './Scholarships.styles';

export const Scholarships = () => {
  const location: CollegeLocation = useLocation();
  const navigate = useNavigate();
  const currentHash = location.hash.slice(1);

  const [pageMode, setPageMode] = useState(FROM_HASH_TO_MODE[currentHash] || PAGE_TABS_DATA.allScholarships.mode);

  const pageBreadcrumbs = getBreadcrumbs(location.state?.from, BREADCRUMBS_PAGE_TEXT.SCHOLARSHIPS);

  const handlePageModeChange = (event: SyntheticEvent, newValue: number) => {
    setPageMode(newValue);

    navigate(`${location.pathname}#${FROM_MODE_TO_HASH[newValue]}`);
  };

  useEffect(() => {
    if (Object.values(PAGE_TABS_DATA).some((item) => item.hash === currentHash)) {
      setPageMode(FROM_HASH_TO_MODE[currentHash]);

      return;
    }

    navigate(`${location.pathname}#${PAGE_TABS_DATA.allScholarships.hash}`, { state: location.state });
    setPageMode(PAGE_TABS_DATA.allScholarships.mode);
  }, [currentHash]);

  const getCurrentComponent = () => {
    const tabData = PAGE_TABS_DATA[currentHash];
    if (tabData?.component) {
      const Component = tabData.component;
      return <Component />;
    }
    return null;
  };

  return (
    <CoreBox sx={sxStyles.pageBg}>
      <CoreBox sx={sxStyles.headSectionWrap}>
        <Container>
          <Breadcrumbs links={pageBreadcrumbs.links} currentPage={pageBreadcrumbs.currentPage} />

          <StyledNavBar>
            <CoreBox sx={sxStyles.navBarInfo}>
              <StyledNavBarTitle data-testid="navBarTitle" variant="h2" component="h1">
                Explore the scholarships for you
              </StyledNavBarTitle>

              <CoreTypography data-testid="navBarDescription">
                To find the scholarship that fits you the best, you can search and filter by your needs. You can add
                scholarships to your favorites list or apply to scholarships directly on their website.
              </CoreTypography>
            </CoreBox>

            <ExploreModeSwitcher showSwitcher={pageMode === PAGE_TABS_DATA.allScholarships.mode} />
          </StyledNavBar>

          <StyledTabs value={pageMode} onChange={handlePageModeChange}>
            {Object.values(PAGE_TABS_DATA).map(({ mode, name }) => {
              return <Tab key={mode} label={name} />;
            })}
          </StyledTabs>
        </Container>
      </CoreBox>

      <StyledContentSection>{getCurrentComponent()}</StyledContentSection>
    </CoreBox>
  );
};
